import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Window,
  WindowHeader,
  WindowContent,
  Tabs,
  Tab,
  TabBody,
  Table,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableDataCell,
  Checkbox,
  Button
} from 'react95';
import classNames from 'classnames';
import { isArray, isObject, first, get, isNil, isEmpty, orderBy, map, sortBy } from 'lodash-es';
import { tpInfoVisibleSelector } from '../../selectors/taskDetailSelectors';
import { setTaskDetailResMsg, setTpInfoVisible } from '../../actions/taskDetailActions';
import { getTaskFieldDisplayVal } from '../../utils/taskUtils';
import { FORM_UPLOAD_COLS, TP_INFO_COLS } from '../../global/Environment';
import './TaxpayerInfoTable.scss';
import TaxpayerInfoFormUploadModal from './TaxpayerInfoFormUploadModal';
import { sentryCaptureException } from '../../utils/sentryUtils';
import { useParams } from 'react-router-dom';
import { useGetTaskFormsQuery, useGetTaskQuery } from '../../api/opsSystemApi';
import { useQDeckUserEmailNameMap } from '../../api/qdeckApi';

const SUCCESSFUL_UPLOAD_STATUSES = [
  'processed_manually',
  'processed_by_ocr',
  'prefilled',
  'composite_prefilled',
  'compound_prefilled',
  'recorded',
  'chat'
];

const TaxpayerInfoTable = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [showAllUploads, setShowAllUploads] = useState(false);
  const [formUploadModalOpen, setFormUploadModalOpen] = useState(false);

  const tpInfoVisible = useSelector(tpInfoVisibleSelector);

  const dispatch = useDispatch();

  const { qdeckUserEmailNameMap, isLoading: isQDeckUserEmailNameMapLoading } = useQDeckUserEmailNameMap();

  const { taskId } = useParams();
  const { data: task = {}, isLoading: isTaskLoading } = useGetTaskQuery(taskId);
  const { data: forms = [], isLoading: areFormsLoading } = useGetTaskFormsQuery(taskId);

  if (isTaskLoading || isQDeckUserEmailNameMapLoading || areFormsLoading) {
    return null;
  }

  const filteredForms = showAllUploads
    ? forms
    : forms.filter((upload) => {
        return SUCCESSFUL_UPLOAD_STATUSES.includes(upload.status);
      });

  /** Formats the value of a taxpayer info field */
  const formatTaxpayerInfoValue = (field, task) => {
    if (!field.startsWith('multistate') && !field.startsWith('healthcare')) {
      if (isArray(get(task, field))) {
        if (isObject(first(get(task, field)))) {
          return map(get(task, field), (item) => `${JSON.stringify(item)}\n`);
        }
        return get(task, field).join(', ');
      } else if (isObject(get(task, field))) {
        return JSON.stringify(get(task, field));
      }
    }
    return getTaskFieldDisplayVal(field, task, qdeckUserEmailNameMap);
  };

  const isPremiumUser = get(task, ['user', 'pricing']) && task.user.pricing.startsWith('premium');

  return (
    <Window className={classNames('tp-info-container-window', { hidden: !tpInfoVisible })}>
      <div className='tp-info-container-window-inner'>
        <WindowHeader className='tp-info-container-title'>
          <div className='tp-info-container-title-text'>
            <div>Taxpayer Info - TASK TAX YEAR: {task.year}</div>
            {isNil(get(task, ['user', 'id'])) && (
              <div className='tp-info-container-title-text-warning'>[ USER DELETED ACCOUNT ]</div>
            )}
            {isPremiumUser && <div className='tp-info-container-title-text-premium'>[ Premium user ]</div>}
            {get(task, ['user', 'pricing']) && task.user.pricing.startsWith('business') && (
              <div className='tp-info-container-title-text-premium'>[ Business user ]</div>
            )}
          </div>

          <Button primary square size='sm' variant='flat' onClick={() => dispatch(setTpInfoVisible(!tpInfoVisible))}>
            -
          </Button>
        </WindowHeader>
        <WindowContent className='tp-info-container-content'>
          <Tabs value={activeTab} onChange={(val) => setActiveTab(val)}>
            <Tab className={classNames('tp-info-container-tab', { selected: !activeTab })} value={0}>
              TP info
            </Tab>
            <Tab className={classNames('tp-info-container-tab', { selected: activeTab === 1 })} value={1}>
              Form uploads
            </Tab>
          </Tabs>

          <TabBody>
            {!activeTab && (
              <div className='tp-info-container-content-default'>
                <Table>
                  <TableBody>
                    {TP_INFO_COLS.map(({ displayName, field }) => (
                      <TableRow key={field}>
                        <TableDataCell className='tp-info-label'>
                          <div>{displayName}</div>
                        </TableDataCell>
                        <TableDataCell
                          className={
                            // Highlight premium situations
                            (field === 'premiumSituations' && get(task, field, []).length > 0) ||
                            // Highlight tax notes for premium users
                            (isPremiumUser && field === 'taxNotes' && !isEmpty(formatTaxpayerInfoValue(field, task)))
                              ? 'tp-info-container-content-premium'
                              : ''
                          }>
                          <div>{formatTaxpayerInfoValue(field, task)}</div>
                        </TableDataCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {activeTab === 1 && (
              <>
                <div className='tp-info-container-content-buttons-wrapper'>
                  <Checkbox
                    label='Show All Uploads'
                    checked={showAllUploads}
                    onChange={() => setShowAllUploads(!showAllUploads)}
                  />
                  <Button
                    primary
                    variant='flat'
                    className='tp-info-container-content-upload-forms-button'
                    onClick={() => {
                      setFormUploadModalOpen(true);
                    }}>
                    {'Upload Forms'}
                  </Button>
                </div>
                <div className='tp-info-container-content-forms'>
                  <div className='tp-info-container-content-forms-wrapper'>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeadCell key='download' className='forms-download-col'>
                            Actions
                          </TableHeadCell>
                          {FORM_UPLOAD_COLS.map(({ displayName }) => (
                            <TableHeadCell key={displayName}>{displayName}</TableHeadCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {!isEmpty(filteredForms) &&
                          orderBy(
                            filteredForms,
                            [
                              (upload) => {
                                return SUCCESSFUL_UPLOAD_STATUSES.indexOf(upload.status);
                              },
                              'updatedAt'
                            ],
                            ['desc', 'desc']
                          ).flatMap((record) =>
                            sortBy(record.files, (file) => file.page).map((file) => (
                              <TableRow key={file.id}>
                                <TableDataCell key={file.id + 'download'} className='forms-download-col'>
                                  {record.status === 'cloud_upload_failed' ? (
                                    <div className='forms-download-content-text-wrapper'>
                                      <p>None</p>
                                    </div>
                                  ) : (
                                    <Button
                                      primary
                                      variant='flat'
                                      size='sm'
                                      onClick={() => {
                                        if (file.signedUrl) {
                                          // Extract expiration data from the signed URL to make sure the link is still valid
                                          const params = new URLSearchParams(file.signedUrl);
                                          const dateString = params.get('X-Amz-Date');
                                          const isoDateString = dateString.replace(
                                            /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/,
                                            '$1-$2-$3T$4:$5:$6Z'
                                          );
                                          const date = new Date(isoDateString);
                                          const expiresInSeconds = Number(params.get('X-Amz-Expires'));
                                          const expirationDate = new Date(date.getTime() + expiresInSeconds * 1000);
                                          if (expirationDate > new Date()) {
                                            window.open(file.signedUrl, '_blank');
                                          } else {
                                            sentryCaptureException(new Error('File has expired'));
                                            dispatch(
                                              setTaskDetailResMsg({
                                                error: 'Form URL is expired. Please reload the page and try again.'
                                              })
                                            );
                                          }
                                        } else {
                                          sentryCaptureException(new Error('File has no signed URL'));
                                          dispatch(
                                            setTaskDetailResMsg({
                                              error: 'Error opening form. Please try again.'
                                            })
                                          );
                                        }
                                      }}>
                                      Download
                                    </Button>
                                  )}
                                </TableDataCell>
                                {FORM_UPLOAD_COLS.map(({ getValue, displayName }) => (
                                  <TableDataCell
                                    key={file.id + displayName}
                                    className={classNames({
                                      'forms-download-val-premium':
                                        displayName === 'Is Premium' && getValue(record, file) === 'Yes'
                                    })}>
                                    <div>{getValue(record, file)}</div>
                                  </TableDataCell>
                                ))}
                              </TableRow>
                            ))
                          )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}
          </TabBody>
        </WindowContent>
      </div>
      {formUploadModalOpen && (
        <TaxpayerInfoFormUploadModal
          onClose={() => {
            setFormUploadModalOpen(false);
          }}
        />
      )}
    </Window>
  );
};

export default TaxpayerInfoTable;
